<template>
  <BasePill
    :text="text"
    :hoverable="hoverable"
    :selected="selected"
    :size="size"
    :theme="theme"
    :class="`variant-${variant}`"
  />
</template>

<script>
import BasePill from '@/components/library/pills/BasePill.vue';

const sizes = [
  'normal',
  'large',
];

const variants = {
  inProgress: {
    defaultContainerClasses: 'variant-inProgress border-signableBrandColours-midGrey',
    defaultCircleClasses: 'bg-envelopeStatus-inProgress',
    defaultTextClasses: 'text-black',
    hoverContainerClasses: 'hover:border-envelopeStatus-inProgress focus-visible:border-envelopeStatus-inProgress',
    hoverCircleClasses: 'group-hover:bg-envelopeStatus-inProgress group-focus-visible:bg-envelopeStatus-inProgress',
    hoverTextClasses: 'group-hover:text-dark3 group-focus-visible:text-dark3',
    selectedContainerClasses: 'border-envelopeStatus-inProgress bg-envelopeStatus-inProgress',
    selectedCircleClasses: 'bg-whiteOverride',
    selectedTextClasses: 'text-whiteOverride',
  },
  draft: {
    defaultContainerClasses: 'variant-draft border-signableBrandColours-midGrey',
    defaultCircleClasses: 'bg-envelopeStatus-draft',
    defaultTextClasses: 'text-black',
    hoverSvgClasses: 'fill-none lg:group-hover:fill-black group-focus-visible:fill-black', // TODO remove 'lg:' after upgrading to Tailwind V3 - can replace with hoverOnlyWhenSupported
    hoverContainerClasses: 'hover:border-envelopeStatusDark-draft focus-visible:border-envelopeStatusDark-draft',
    hoverCircleClasses: 'group-hover:bg-envelopeStatus-draft group-focus-visible:bg-envelopeStatus-draft',
    hoverTextClasses: 'group-hover:text-dark3 group-focus-visible:text-dark3',
    selectedContainerClasses: 'border-envelopeStatus-draft bg-envelopeStatus-draft',
    selectedCircleClasses: 'bg-whiteOverride',
    selectedTextClasses: 'text-dark3',
  },
  signed: {
    defaultContainerClasses: 'variant-signed border-signableBrandColours-midGrey',
    defaultCircleClasses: 'bg-envelopeStatus-signed',
    defaultTextClasses: 'text-black',
    hoverContainerClasses: 'hover:border-envelopeStatus-signed focus-visible:border-envelopeStatus-signed',
    hoverCircleClasses: 'group-hover:bg-envelopeStatus-signed group-focus-visible:bg-envelopeStatus-signed',
    hoverTextClasses: 'group-hover:text-dark3 group-focus-visible:text-dark3',
    selectedContainerClasses: 'border-envelopeStatus-signed bg-envelopeStatus-signed',
    selectedCircleClasses: 'bg-whiteOverride',
    selectedTextClasses: 'text-dark3',
  },
  rejected: {
    defaultContainerClasses: 'variant-rejected border-signableBrandColours-midGrey',
    defaultCircleClasses: 'bg-envelopeStatus-rejected',
    defaultTextClasses: 'text-black',
    hoverContainerClasses: 'hover:border-envelopeStatus-rejected focus-visible:border-envelopeStatus-rejected',
    hoverCircleClasses: 'group-hover:bg-envelopeStatus-rejected group-focus-visible:bg-envelopeStatus-rejected',
    hoverTextClasses: 'group-hover:text-dark3 group-focus-visible:text-dark3',
    selectedContainerClasses: 'border-envelopeStatus-rejected bg-envelopeStatus-rejected',
    selectedCircleClasses: 'bg-whiteOverride',
    selectedTextClasses: 'text-whiteOverride',
  },
  unverified: {
    defaultContainerClasses: 'variant-unverified border-signableBrandColours-midGrey',
    defaultCircleClasses: 'bg-envelopeStatus-unverified',
    defaultTextClasses: 'text-black',
    hoverContainerClasses: 'hover:border-envelopeStatus-unverified focus-visible:border-envelopeStatus-unverified',
    hoverCircleClasses: 'group-hover:bg-envelopeStatus-unverified group-focus-visible:bg-envelopeStatus-unverified',
    hoverTextClasses: 'group-hover:text-dark3 group-focus-visible:text-dark3',
    selectedContainerClasses: 'border-envelopeStatus-unverified bg-envelopeStatus-unverified',
    selectedCircleClasses: 'bg-whiteOverride',
    selectedTextClasses: 'text-whiteOverride',
  },
  expired: {
    defaultContainerClasses: 'variant-expired border-signableBrandColours-midGrey',
    defaultCircleClasses: 'bg-envelopeStatus-expired',
    defaultTextClasses: 'text-black',
    hoverContainerClasses: 'hover:border-envelopeStatus-expired focus-visible:border-envelopeStatus-expired',
    hoverCircleClasses: 'group-hover:bg-envelopeStatus-expired group-focus-visible:bg-envelopeStatus-expired',
    hoverTextClasses: 'group-hover:text-dark3 group-focus-visible:text-dark3',
    selectedContainerClasses: 'border-envelopeStatus-expired bg-envelopeStatus-expired',
    selectedCircleClasses: 'bg-whiteOverride',
    selectedTextClasses: 'text-whiteOverride',
  },
  cancelled: {
    defaultContainerClasses: 'variant-cancelled border-signableBrandColours-midGrey',
    defaultCircleClasses: 'bg-envelopeStatus-cancelled',
    defaultTextClasses: 'text-black',
    hoverContainerClasses: 'hover:border-envelopeStatus-cancelled focus-visible:border-envelopeStatus-cancelled',
    hoverCircleClasses: 'group-hover:bg-envelopeStatus-cancelled group-focus-visible:bg-envelopeStatus-cancelled',
    hoverTextClasses: 'group-hover:text-dark3 group-focus-visible:text-dark3',
    selectedContainerClasses: 'border-envelopeStatus-cancelled bg-envelopeStatus-cancelled',
    selectedCircleClasses: 'bg-whiteOverride',
    selectedTextClasses: 'text-whiteOverride',
  },
};

export default {
  name: 'EnvelopeStatusPill',
  components: {
    BasePill,
  },
  props: {
    variant: {
      type: String,
      required: true,
      validator(value) {
        return Object.keys(variants).includes(value);
      },
    },
    text: {
      type: String,
      required: true,
    },
    hoverable: {
      type: Boolean,
      required: false,
      default: false,
    },
    selected: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'normal',
      validator(value) {
        return sizes.includes(value);
      },
    },
  },
  computed: {
    theme() {
      return variants[this.variant];
    },
  },
};
</script>
